import type { UnifiedCanvas } from '../modes/unified/UnifiedCanvas';
import type { ProjectStorage } from '../storage/ProjectStorage';
import type { AppMode } from '../types';
import { createBlankCanvasDataUrl } from '../utils/export';
import { loadLastSessionFromStorage } from './sessionLoad';

export interface SessionContext {
  getStorage: () => ProjectStorage | null;
  getMode: () => AppMode;
  getCurrentProjectId: () => string | null;
  setCurrentProjectId: (id: string | null) => void;
  getCurrentProjectName: () => string;
  setCurrentProjectName: (name: string) => void;
  getUnifiedCanvas: () => UnifiedCanvas | null;
  getUnifiedState: () => string | null;
  setUnifiedState: (state: string | null) => void;
  setCurrentProjectSelection: (id: string, name: string) => void;
  refreshProjectList: () => Promise<void>;
  loadProject: (id: string) => Promise<void>;
  switchMode: (mode: AppMode, preserveCurrentState?: boolean) => void;
}

const THUMBNAIL_SIZE = { width: 200, height: 112 };

export class SessionController {
  private readonly context: SessionContext;
  private readonly autoSaveDelay: number;
  private readonly lastSessionKey: string;
  private autoSaveTimeout: ReturnType<typeof setTimeout> | null = null;

  private isLoadingProject = false;
  private lastSavedState: string | null = null;
  private saveQueue: Promise<void> = Promise.resolve();



  constructor(context: SessionContext, options?: { autoSaveDelay?: number; lastSessionKey?: string }) {
    this.context = context;
    this.autoSaveDelay = options?.autoSaveDelay ?? 500;
    this.lastSessionKey = options?.lastSessionKey ?? 'drawlings-last-session';
  }

  setLoading(loading: boolean): void {
    this.isLoadingProject = loading;
  }

  cancelAutoSave(): void {
    if (this.autoSaveTimeout) {
      clearTimeout(this.autoSaveTimeout);
      this.autoSaveTimeout = null;
    }
  }

  scheduleAutoSave(onSave?: () => void): void {
    this.cancelAutoSave();
    this.autoSaveTimeout = setTimeout(() => {
      if (onSave) {
        onSave();
      } else {
        void this.performAutoSave();
      }
    }, this.autoSaveDelay);
  }

  resetLastSavedStates(): void {
    this.lastSavedState = null;
  }

  setLastSavedStates(drawState: string | null, _diagramState: string | null): void {
    // With unified canvas, we use a single state. For backward compat during migration,
    // accept both params but only track one.
    this.lastSavedState = drawState;
  }

  async performAutoSave(forceRefreshList = false, forceThumbnailUpdate = false): Promise<void> {
    const storage = this.context.getStorage();
    if (!storage || this.isLoadingProject) return;

    // Capture IDs synchronously before entering the serial queue
    const projectId = this.context.getCurrentProjectId();
    const projectNameCapture = this.context.getCurrentProjectName();
    const mode = this.context.getMode();

    this.saveQueue = this.saveQueue.then(async () => {
      try {
        let projectName = projectNameCapture;
        let thumbnail: string | undefined;

        const canvas = this.context.getUnifiedCanvas();

        let currentState = canvas?.getState() ?? this.context.getUnifiedState();
        const hasContent = this.checkContent(currentState);

        if (canvas) {
          thumbnail = canvas.getCanvasDataUrl();
        }

        if (!hasContent && !projectId) {
          return;
        }

        const stateChanged = currentState !== this.lastSavedState;
        const config = canvas?.getConfig() ?? {
          width: 1920,
          height: 1080,
          backgroundColor: '#ffffff',
        };

        if (stateChanged || !projectId || forceThumbnailUpdate) {
          if (!projectName || projectName === 'Untitled') {
            projectName = this.generateSessionName();
            this.context.setCurrentProjectName(projectName);
          }

          const isNewProject = !projectId;
          // Store unified state in drawModeState field for backward compat
          const project = await storage.saveProject(
            projectId,
            projectName,
            thumbnail || this.createEmptyThumbnail(),
            config,
            currentState ?? undefined,
            undefined,
            mode
          );

          this.lastSavedState = currentState;

          if (this.context.getCurrentProjectId() === projectId) {
            this.context.setCurrentProjectId(project.id);

            // Only update current name if it hasn't changed since we started saving
            if (this.context.getCurrentProjectName() === projectName) {
              this.context.setCurrentProjectName(project.name);
            }

            if (isNewProject || forceRefreshList) {
              this.context.setCurrentProjectSelection(project.id, project.name);
              await this.context.refreshProjectList();
            }

            this.saveLastSession();
          }

        }
      } finally {
        // No-op
      }
    });

    return this.saveQueue;
  }



  async loadLastSession(): Promise<void> {
    await loadLastSessionFromStorage({
      storageKey: this.lastSessionKey,
      getStorage: this.context.getStorage,
      getMode: this.context.getMode,
      loadProject: this.context.loadProject,
      switchMode: this.context.switchMode,
    });
  }

  saveLastSession(): void {
    const projectId = this.context.getCurrentProjectId();
    const sessionData = JSON.stringify({
      projectId: projectId ?? null,
      mode: this.context.getMode(),
    });
    localStorage.setItem(this.lastSessionKey, sessionData);
  }

  clearLastSession(): void {
    localStorage.removeItem(this.lastSessionKey);
  }

  createEmptyThumbnail(): string {
    return createBlankCanvasDataUrl(
      THUMBNAIL_SIZE.width,
      1,
      '#ffffff',
      THUMBNAIL_SIZE.height,
      0.7
    );
  }

  generateSessionName(): string {
    const now = new Date();
    const month = now.toLocaleDateString(undefined, { month: 'short' });
    const day = now.getDate();
    const time = now.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    return `Session ${month} ${day}, ${time}`;
  }

  private checkContent(state: string | null): boolean {
    if (!state) return false;
    try {
      const parsed = JSON.parse(state);
      return (parsed.strokes?.length > 0) ||
        (parsed.shapes?.length > 0) ||
        (parsed.connectors?.length > 0) ||
        (parsed.textElements?.length > 0) ||
        (parsed.drawShapes?.length > 0);
    } catch {
      return false;
    }
  }
}
